import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Button } from "./ui/button";
import {
  activities,
  activityMetaDate,
  CATEGORY_LABELS,
} from "../data/activities-data";
import ReserveModal from "./ReserveModal";
import transition from "./transition";
import "../style/ActivityStory.css";

/** Fades content up as it scrolls into view — same feel as the About page. */
const Reveal = ({ children, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px 0px" }}
    transition={{ duration: 0.5, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

/** Renders one block from an activity's `body` array. */
const StoryBlock = ({ block, storyTitle }) => {
  switch (block.type) {
    case "text":
      return (
        <Reveal className="story-text">
          <p>{block.text}</p>
        </Reveal>
      );

    case "heading":
      return (
        <Reveal className="story-text">
          <h2 className="story-heading">{block.text}</h2>
        </Reveal>
      );

    case "quote":
      return (
        <Reveal className="story-quote">
          <blockquote>{block.text}</blockquote>
          {block.attribution && (
            <p className="story-quote-attribution">{block.attribution}</p>
          )}
        </Reveal>
      );

    case "image":
      return (
        <Reveal className="story-figure">
          <figure>
            <img
              className="story-image"
              src={block.src}
              alt={block.alt || storyTitle}
              loading="lazy"
            />
            {block.caption && <figcaption>{block.caption}</figcaption>}
          </figure>
        </Reveal>
      );

    case "imagePair":
      return (
        <Reveal className="story-figure">
          <figure>
            <div className="story-pair">
              <img
                className="story-image"
                src={block.left.src}
                alt={block.left.alt || storyTitle}
                loading="lazy"
              />
              <img
                className="story-image"
                src={block.right.src}
                alt={block.right.alt || storyTitle}
                loading="lazy"
              />
            </div>
            {block.caption && <figcaption>{block.caption}</figcaption>}
          </figure>
        </Reveal>
      );

    case "video":
      return (
        <Reveal className="story-figure">
          <figure>
            <div className="story-video">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${block.videoId}?rel=0&showinfo=0`}
                title={block.caption || storyTitle}
                frameBorder="0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
            {block.caption && <figcaption>{block.caption}</figcaption>}
          </figure>
        </Reveal>
      );

    default:
      return null;
  }
};

const ActivityStory = () => {
  const { id } = useParams();
  const [reserveOpen, setReserveOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  const activity = activities.find((a) => a.id === id);
  if (!activity) return <Navigate to="/activities" replace />;

  const detail = [activity.location, activity.time].filter(Boolean).join("  ·  ");
  const more = activities
    .filter((a) => a.id !== id)
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
    .slice(0, 3);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const max = el.scrollHeight - el.clientHeight;
    setProgress(max > 0 ? el.scrollTop / max : 0);
  };

  return (
    <div className="story-page" onScroll={handleScroll}>
      <div className="story-progress">
        <div
          className="story-progress-bar"
          style={{ transform: `scaleX(${progress})` }}
        />
      </div>

      {reserveOpen && (
        <ReserveModal
          title={`${activity.title} — ${activityMetaDate(activity)}`}
          onClose={() => setReserveOpen(false)}
        />
      )}

      <div className="story-inner">
        <Link to="/activities" className="story-back">
          ← All Activities
        </Link>

        <header className="story-header">
          <div className="story-meta">
            <span className="story-date">{activityMetaDate(activity)}</span>
            {activity.categories.map((c) => (
              <span key={c} className="story-tag">
                {CATEGORY_LABELS[c]}
              </span>
            ))}
          </div>

          <h1 className="story-title">{activity.title}</h1>

          {activity.description && (
            <p className="story-standfirst">{activity.description}</p>
          )}

          {detail && <p className="story-detail">{detail}</p>}

          {activity.reserve ? (
            <Button
              variant="editorial"
              size="lg"
              disabled={activity.isFull}
              onClick={() => setReserveOpen(true)}
            >
              {activity.isFull ? "Class is Full" : "Reserve Spot"}
              {!activity.isFull && <ArrowUpRight className="activity-icon" />}
            </Button>
          ) : activity.link ? (
            <a
              href={activity.link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="activity-link"
            >
              <Button variant="editorial" size="lg">
                {activity.link.label}
                <ArrowUpRight className="activity-icon" />
              </Button>
            </a>
          ) : null}
        </header>

        {activity.image && (
          <Reveal className="story-hero">
            <img
              className="story-image story-hero-img"
              src={activity.image}
              alt={activity.title}
            />
          </Reveal>
        )}

        <div className="story-body">
          {(activity.body || []).map((block, i) => (
            <StoryBlock key={i} block={block} storyTitle={activity.title} />
          ))}
        </div>

        <footer className="story-more">
          <p className="story-more-label">More Activities</p>
          {more.map((a) => (
            <Link
              key={a.id}
              to={a.body ? `/activities/${a.id}` : "/activities"}
              className="story-more-row"
            >
              <span className="story-more-date">{activityMetaDate(a)}</span>
              <span className="story-more-title">{a.title}</span>
            </Link>
          ))}
        </footer>
      </div>
    </div>
  );
};

export default transition(ActivityStory);
